
import { useAppSelector } from './useAppSelector';
import { useAppDispatch } from './useAppDispatch';
import { useEffect } from 'react';
import {
  setSelectedCategories,
  setSelectedEmployees,
  setSelectedProductCount,
  setSelectedSubcategories,
  setSelectedModalities,
  setSelectedTags,
  setSelectedDiscounts,
  setSelectedAddOns,
  setSlugFilter
} from '../store/companiesFilterSlice';
import { SupabaseCompany } from '@/utils/types/companyTypes';
import { useTags } from './useTags';
import { updateCompaniesWithPlans } from '@/utils/companiesHelper';

interface TagObject {
  id?: string;
  name?: string;
  description?: string;
  keywords?: string[] | string;
  tag?: {
    id?: string;
    name?: string;
  };
}

export const useCompaniesFilter = (displayResults: SupabaseCompany[]) => {
  const dispatch = useAppDispatch();
  const companiesFilterState = useAppSelector(state => state.companiesFilter);
  const {
    selectedCategories = ['All'],
    selectedEmployees = ['All'],
    selectedProductCount = ['All'],
    selectedSubcategories = ['All'],
    selectedModalities = ['All'],
    selectedTags = ['All'],
    selectedDiscounts = ['All'],
    selectedAddOns = ['All'],
    slugFilter = ''
  } = companiesFilterState || {};

  const handleCategoryChange = (value: string | string[]) => {
    dispatch(setSelectedCategories(Array.isArray(value) ? value : [value]));
  };

  const handleEmployeesChange = (value: string | string[]) => {
    dispatch(setSelectedEmployees(Array.isArray(value) ? value : [value]));
  };

  const handleProductCountChange = (value: string | string[]) => {
    dispatch(setSelectedProductCount(Array.isArray(value) ? value : [value]));
  };

  const handleSubcategoryChange = (value: string | string[]) => {
    dispatch(setSelectedSubcategories(Array.isArray(value) ? value : [value]));
  };

  const handleModalityChange = (value: string | string[]) => {
    dispatch(setSelectedModalities(Array.isArray(value) ? value : [value]));
  };

  const handleTagChange = (value: string | string[]) => {
    dispatch(setSelectedTags(Array.isArray(value) ? value : [value]));
  };

  const handleDiscountChange = (value: string | string[]) => {
    dispatch(setSelectedDiscounts(Array.isArray(value) ? value : [value]));
  };

  const handleAddOnsChange = (value: string | string[]) => {
    dispatch(setSelectedAddOns(Array.isArray(value) ? value : [value]));
  };

  const handleSlugFilterChange = (value: string) => {
    dispatch(setSlugFilter(value));
  };

  const handleRemoveFilter = (
    type: 'category' | 'employees' | 'productCount' | 'subcategory' | 'modality' | 'tag' | 'discount' | 'addons' | 'slug',
    valueToRemove?: string
  ) => {
    if (type === 'category') {
      if (valueToRemove) {
        const filtered = selectedCategories.filter(cat => cat !== valueToRemove);
        dispatch(setSelectedCategories(filtered.length === 0 ? ['All'] : filtered));
      } else {
        dispatch(setSelectedCategories(['All']));
      }
    } else if (type === 'employees') {
      if (valueToRemove) {
        const filtered = selectedEmployees.filter(emp => emp !== valueToRemove);
        dispatch(setSelectedEmployees(filtered.length === 0 ? ['All'] : filtered));
      } else {
        dispatch(setSelectedEmployees(['All']));
      }
    } else if (type === 'productCount') {
      if (valueToRemove) {
        const filtered = selectedProductCount.filter(count => count !== valueToRemove);
        dispatch(setSelectedProductCount(filtered.length === 0 ? ['All'] : filtered));
      } else {
        dispatch(setSelectedProductCount(['All']));
      }
    } else if (type === 'subcategory') {
      if (valueToRemove) {
        const filtered = selectedSubcategories.filter(sub => sub !== valueToRemove);
        dispatch(setSelectedSubcategories(filtered.length === 0 ? ['All'] : filtered));
      } else {
        dispatch(setSelectedSubcategories(['All']));
      }
    } else if (type === 'modality') {
      if (valueToRemove) {
        const filtered = selectedModalities.filter(mod => mod !== valueToRemove);
        dispatch(setSelectedModalities(filtered.length === 0 ? ['All'] : filtered));
      } else {
        dispatch(setSelectedModalities(['All']));
      }
    } else if (type === 'tag') {
      if (valueToRemove) {
        const filtered = selectedTags.filter(tag => tag !== valueToRemove);
        dispatch(setSelectedTags(filtered.length === 0 ? ['All'] : filtered));
      } else {
        dispatch(setSelectedTags(['All']));
      }
    } else if (type === 'discount') {
      if (valueToRemove) {
        const filtered = selectedDiscounts.filter(discount => discount !== valueToRemove);
        dispatch(setSelectedDiscounts(filtered.length === 0 ? ['All'] : filtered));
      } else {
        dispatch(setSelectedDiscounts(['All']));
      }
    } else if (type === 'addons') {
      if (valueToRemove) {
        const filtered = selectedAddOns.filter(addon => addon !== valueToRemove);
        dispatch(setSelectedAddOns(filtered.length === 0 ? ['All'] : filtered));
      } else {
        dispatch(setSelectedAddOns(['All']));
      }
    } else if (type === 'slug') {
      dispatch(setSlugFilter(''));
    }
  };

  // Extract unique subcategories and modalities from companies
  const uniqueSubcategories = ['All', 'None', ...Array.from(new Set(
    displayResults.flatMap(company =>
      company.pricingMetrics?.map(metric => metric.subcategory) || []
    ).filter(Boolean) as string[]
  ))];

  // Only include specific modalities (Credit, Usage, License)
  const allowedModalities = ['Credit', 'Usage', 'License'];
  const uniqueModalities = ['All', ...allowedModalities, 'None'];

  // Fetch tags directly from the tags table
  const { tags: allTags, loading: tagsLoading } = useTags();

  // Extract unique tags from the tags table
  const uniqueTags = ['All', ...allTags.map(tag => tag.name)];

  // Helper function to format discount type for display
  const formatDiscountType = (discountType: string): string => {
    if (discountType === 'All') return 'All';
    
    // Remove _discount suffix and replace underscores with spaces
    const formatted = discountType
      .replace(/_discount$/, '')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return formatted;
  };

  // Define available discount types based on the GraphQL schema
  const discountTypes = [
    'All',
    'intro_discount',
    'startup_discount',
    'education_discount',
    'multi_product_discount',
    'open_source_discount',
    'multi_year_discount',
    'volume_discount',
    'migration_discount',
    'nonprofit_discount',
    'annual_discount'
  ];

  useEffect(() => {
    // Log tag structure from a few companies
    const sampleCompanies = displayResults.slice(0, 3); // First three companies

    // console.log('🔍 [useCompaniesFilter] DEBUG TAG STRUCTURE:');
    sampleCompanies.forEach(company => {
      if (company.tags && company.tags.length > 0) {
        console.log(`Company ${company.slug} has ${company.tags.length} tags:`, {
          tags: company.tags,
          firstTagType: typeof company.tags[0],
          firstTagStructure: JSON.stringify(company.tags[0], null, 2)
        });
      }
    });

    // Compare the allTags structure with company tags structure
    // console.log('🔍 [useCompaniesFilter] Tags structure comparison:', {
    //   'allTags from useTags()': allTags.slice(0, 3),
    //   'uniqueTags for dropdown': uniqueTags.slice(0, 5),
    //   'Tags from Companies.tsx': sampleCompanies.find(c => c.tags?.length > 0)?.tags?.slice(0, 2) || []
    // });

    // Extract unique tag names from all companies for debugging
    const allCompanyTagNames = new Set();
    displayResults.forEach(company => {
      if (company.tags && Array.isArray(company.tags)) {
        company.tags.forEach(tag => {
          if (typeof tag === 'string') allCompanyTagNames.add(tag);
          else if (tag?.name) allCompanyTagNames.add(tag.name);
        });
      }
    });
    // console.log('🏷️ [useCompaniesFilter] Unique tag names across all companies:', [...allCompanyTagNames]);
  }, [displayResults, allTags]);

  // Load plans data when add-ons filter is used
  useEffect(() => {
    // Check if add-ons filter is being used and we don't have plans data
    if (selectedAddOns && !selectedAddOns.includes('All') && displayResults.length > 0) {
      const hasPlansData = displayResults.some(company => 
        company.pages?.some(page => page.plans && page.plans.length > 0)
      );
      
      if (!hasPlansData) {
        console.log('📋 [useCompaniesFilter] Loading plans data for add-ons filter...');
        updateCompaniesWithPlans().catch(error => {
          console.error('❌ [useCompaniesFilter] Failed to load plans data:', error);
        });
      }
    }
  }, [selectedAddOns, displayResults]);

  // Debug logging for tags
  // console.log('📋 [useCompaniesFilter] Tag filtering setup:', {
  //   uniqueTags,
  //   selectedTags,
  //   tagsLoading
  // });

  // Debug logging for companies with tags
  const companiesWithTags = displayResults.filter(company =>
    company.tags && Array.isArray(company.tags) && company.tags.length > 0
  );

  if (companiesWithTags.length > 0) {
    console.log(
      '🏷️ [useCompaniesFilter] Companies with tags:',
      companiesWithTags.length,
      'Sample tags from first company:',
      companiesWithTags[0].tags.map((tag: any) => {
        if (typeof tag === 'string') return tag;
        if (tag?.name) return tag.name;
        if ((tag as TagObject)?.tag?.name) return (tag as TagObject).tag.name;
        return JSON.stringify(tag);
      })
    );
  } else {
    // console.log('🏷️ [useCompaniesFilter] No companies with tags found');
  }

  const filteredCompanies = displayResults.filter(company => {
    const categoryMatch = selectedCategories.includes('All') ||
      selectedCategories.includes(company.category);

    const employeeMatch = selectedEmployees.includes('All') ||
      selectedEmployees.includes(company.employees);

    // Product count filter - check if company has multiple pages
    const hasMultiplePages = Array.isArray(company.pages) && company.pages.length > 1;

    const productCountMatch = selectedProductCount.includes('All') ||
      (selectedProductCount.includes('Multiple Products') && hasMultiplePages) ||
      (selectedProductCount.includes('Single Product') && !hasMultiplePages);

    // Subcategory filter has been removed, always return true
    const subcategoryMatch = true;

    // Update modality matching to handle 'None' filter option and only consider allowed modalities
    // Use AND operation for multiple selected modalities
    const allowedModalities = ['Credit', 'Usage', 'License'];

    let modalityMatch = true;

    // If 'All' is selected, show all companies
    if (selectedModalities.includes('All')) {
      modalityMatch = true;
    }
    // If 'None' is selected, show companies with no allowed modalities
    else if (selectedModalities.includes('None')) {
      modalityMatch = !company.pricingMetrics ||
             company.pricingMetrics.length === 0 ||
             company.pricingMetrics.every(metric => !metric.modality || !allowedModalities.includes(metric.modality));
    }
    // For multiple selected modalities, use AND operation
    // Company must have ALL selected modalities to match
    else if (selectedModalities.length > 0) {
      // Get all unique modalities for this company that are in the allowed list
      const companyModalities = new Set(
        company.pricingMetrics
          ?.filter(metric => metric.modality && allowedModalities.includes(metric.modality))
          .map(metric => metric.modality) || []
      );

      // Check if ALL selected modalities are present in the company's modalities
      modalityMatch = selectedModalities.every(selectedModality => companyModalities.has(selectedModality));
    }

    // Helper function to normalize a tag
    const normalizeTag = (tag: any): string | null => {
      if (!tag) return null;

      // If tag is a string, return it directly
      if (typeof tag === 'string') {
        return tag;
      }

      // If tag has a name property, return that
      if (tag.name) {
        return tag.name;
      }

      // If tag has a nested tag property, extract the name
      const tagObj = tag as TagObject;
      if (tagObj.tag && tagObj.tag.name) {
        return tagObj.tag.name;
      }

      console.log('🔍 [normalizeTag] Unable to normalize tag:', tag);
      return null;
    };

    // Tag filter - check if company has any of the selected tags
    let tagMatch = false;

    // First check if 'All' is selected, which means show all companies
    if (selectedTags.includes('All')) {
      tagMatch = true;
    } else {
      // Handle case where company.tags might not be an array or might be undefined
      const companyTags = company.tags || [];

      if (Array.isArray(companyTags) && companyTags.length > 0) {
        // Extract all possible tag names from company tags using multiple strategies
        const tagNames: string[] = [];

        companyTags.forEach(tag => {
          // Direct string tag
          if (typeof tag === 'string') {
            tagNames.push(tag);
          }
          // Object with name property
          else if (tag && typeof tag === 'object') {
            if (tag.name) {
              tagNames.push(tag.name);
            }
            // Object with tag.name nested property (from tags_map join)
            else {
              const tagObj = tag as TagObject;
              if (tagObj.tag && tagObj.tag.name) {
                tagNames.push(tagObj.tag.name);
              }
            }
          }
        });

        // Log for debugging
        if (company.slug === 'stripe' || company.slug === 'shopify' || company.slug === 'github') {
          // console.log(`🔍 [useCompaniesFilter] Extracted tag names for ${company.slug}:`, tagNames);
        }

        // Compare tag names with selected tags (case insensitive)
        const lowerSelectedTags = selectedTags.map(t => t.toLowerCase());
        tagMatch = tagNames.some(tagName =>
          lowerSelectedTags.includes(tagName.toLowerCase())
        );
      }

      // Debug logging for specific companies
      if (company.slug === 'stripe' || company.slug === 'shopify' || company.slug === 'github') {
        // console.log(`🔍 [useCompaniesFilter] Tag filtering for ${company.slug}:`, {
        //   selectedTags,
        //   companyTagsCount: Array.isArray(company.tags) ? company.tags.length : 0,
        //   tagMatch
        // });
      }
    }

    // Debug tag filtering
    if (selectedTags.length > 0 && !selectedTags.includes('All') && company.slug === displayResults[0]?.slug) {
      // console.log('🏷️ [useCompaniesFilter] Tag filtering for first company:', {
      //   selectedTags,
      //   companyTags: company.tags?.map(tag => {
      //     if (typeof tag === 'string') return tag;
      //     if (tag?.name) return tag.name;
      //     const tagObj = tag as TagObject;
      //     if (tagObj?.tag?.name) return tagObj.tag.name;
      //     return 'undefined-tag';
      //   }) || [],
      //   tagMatch
      // });
    }

    // Discount filter - check if company has any of the selected discount types
    let discountMatch = false;
    
    if (!selectedDiscounts || selectedDiscounts.length === 0 || selectedDiscounts.includes('All')) {
      discountMatch = true;
    } else {
      // Get all unique discount types for this company
      const companyDiscountTypes = new Set<string>();
      
      // Check if company has a discounts array (from our GraphQL query)
      if (company.discounts && Array.isArray(company.discounts)) {
        company.discounts.forEach((discount: any) => {
          if (discount?.type) {
            companyDiscountTypes.add(discount.type);
          }
        });
      }
      
      // Debug logging for the first few companies
      if (company.slug === 'stripe' || company.slug === 'shopify' || company.slug === 'github') {
        console.log(`🎯 [Discount Filter Debug] ${company.slug}:`, {
          companyDiscounts: company.discounts,
          companyDiscountTypes: Array.from(companyDiscountTypes),
          selectedDiscounts,
          hasMatch: selectedDiscounts.some(selectedDiscount => 
            companyDiscountTypes.has(selectedDiscount)
          )
        });
      }
      
      // Check if company has the selected discount types (using OR operation for multiple selections)
      discountMatch = selectedDiscounts.some(selectedDiscount => 
        companyDiscountTypes.has(selectedDiscount)
      );
    }

    // Add-ons filter - check if company has add-on plans
    let addOnsMatch = false;
    
    if (!selectedAddOns || selectedAddOns.length === 0 || selectedAddOns.includes('All')) {
      addOnsMatch = true;
    } else {
      // Check if company has any plans that are add-ons
      const hasAddOns = company.pages?.some(page => 
        page.plans?.some(plan => plan.add_on === true)
      ) || false;
      
      if (selectedAddOns.includes('Has Add-ons')) {
        addOnsMatch = hasAddOns;
      } else if (selectedAddOns.includes('No Add-ons')) {
        addOnsMatch = !hasAddOns;
      }
    }

    // Slug filter - check if company slug includes the search term (case insensitive)
    const slugMatch = !slugFilter ||
      (company.slug && company.slug.toLowerCase().includes(slugFilter.toLowerCase()));

    // Return true only if all filters match
    return categoryMatch && employeeMatch && productCountMatch && subcategoryMatch && modalityMatch && tagMatch && discountMatch && addOnsMatch && slugMatch;
  });

  return {
    selectedCategories,
    selectedEmployees,
    selectedProductCount,
    selectedSubcategories,
    selectedModalities,
    selectedTags,
    selectedDiscounts,
    selectedAddOns,
    slugFilter,
    uniqueSubcategories,
    uniqueModalities,
    uniqueTags,
    discountTypes,
    formatDiscountType,
    handleCategoryChange,
    handleEmployeesChange,
    handleProductCountChange,
    handleSubcategoryChange,
    handleModalityChange,
    handleTagChange,
    handleDiscountChange,
    handleAddOnsChange,
    handleSlugFilterChange,
    handleRemoveFilter,
    filteredCompanies,
    tagsLoading
  };
};
